// src/pages/orderSuccess.jsx - PHONEPE VERSION

import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchCart } from '../store/slices/cartSlice';
import api from '../store/api/base';
import Loader from '../components/common/loader';

const OrderSuccess = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const refreshCartAndFetchOrder = async () => {
      try {
        // console.log('🔄 Order Success: Refreshing cart from backend');
        await dispatch(fetchCart()).unwrap();
        // console.log('✅ Cart refreshed on order success page');
        
        if (orderId) {
          // console.log('📦 Fetching order details:', orderId);
          const response = await api.get(`/orders/${orderId}`);
          // console.log('✅ Order data received:', response.data);
          
          const orderData = response.data.data?.order || response.data.data || response.data;
          setOrder(orderData);
          // console.log('📋 Order set:', orderData);
        }
        
        setLoading(false);
      } catch (err) {
        // console.error('❌ Error loading order:', err);
        setError('Failed to load order details');
        setLoading(false);
      }
    };
    
    refreshCartAndFetchOrder();
  }, [orderId, dispatch]);
  
  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[400px]">
        <Loader size="lg" />
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p className="font-medium">{error}</p>
          <div className="mt-4">
            <Link to="/orders" className="text-red-900 underline">
              View all orders
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Handle nested pricing object structure
  const pricing = order?.pricing || {};
  const subTotal = pricing.subTotal || order?.subTotal || 0;
  const shippingCost = pricing.shippingCost || order?.shippingCost || 0;
  const gst = pricing.gst || order?.gst || 0;
  const discount = pricing.discount || order?.discount || 0;
  const totalAmount = pricing.totalAmount || order?.totalAmount || (subTotal + shippingCost + gst - discount);
  
  // ✅ Payment method is always RAZORPAY now
  const paymentMethod = order?.payment?.method || 'PHONEPE';

  const paymentStatus = order?.payment?.status || 'COMPLETED';
  const orderStatus = order?.status || 'CONFIRMED';
  
  // console.log('💰 Calculated totals:', {
  //   subTotal,
  //   shippingCost,
  //   gst,
  //   discount,
  //   totalAmount
  // });
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
            <svg 
              className="w-10 h-10 text-green-600" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M5 13l4 4L19 7" 
              />
            </svg>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          
          <p className="text-gray-600">
            Thank you for your order. Your payment has been processed successfully.
          </p>
          
          {/* Payment Confirmation Badge */}
          <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-green-50 border border-green-200 rounded-full">
            <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium text-green-800">Payment Verified</span>
          </div>
        </div>
        
        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="border-b pb-4 mb-4">
            <h2 className="text-xl font-semibold mb-4">Order Details</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Order ID */}
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-600 mb-1">Order ID</p>
                <p className="font-semibold text-blue-600">#{orderId}</p>
              </div>
              
              {/* Order Date */}
              {order?.createdAt && (
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-xs text-gray-600 mb-1">Order Date</p>
                  <p className="font-semibold">
                    {new Date(order.createdAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              )}
              
              {/* Total Amount */}
              <div className="bg-blue-50 p-3 rounded border-2 border-blue-200">
                <p className="text-xs text-gray-600 mb-1">Total Amount Paid</p>
                <p className="font-bold text-2xl text-blue-600">
                  ₹{totalAmount.toLocaleString('en-IN')}
                </p>
              </div>
              
              {/* Payment Method */}
              <div className="bg-green-50 p-3 rounded border border-green-200">
                <p className="text-xs text-gray-600 mb-1">Payment Method</p>
                <p className="font-semibold text-green-800">
                  Online Payment
                </p>
                <p className="text-xs text-green-600 mt-1 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Payment Completed
                </p>
              </div>
              
              {/* Estimated Delivery — set by Shiprocket at order creation */}
{order?.estimatedDelivery && (
  <div className="bg-blue-50 p-3 rounded border border-blue-100">
    <p className="text-xs text-blue-500 mb-1 font-medium uppercase tracking-wide">
      Estimated Delivery
    </p>
    <p className="font-bold text-gray-800">
      {new Date(order.estimatedDelivery).toLocaleDateString('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })}
    </p>
    <p className="text-xs text-blue-400 mt-0.5">
      🚚 Powered by Shiprocket
    </p>
  </div>
)}
              
              {/* Order Status */}
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-xs text-gray-600 mb-1">Order Status</p>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                  orderStatus === 'CONFIRMED' || orderStatus === 'PROCESSING' ? 'bg-green-100 text-green-800' :
                  orderStatus === 'SHIPPED' || orderStatus === 'OUT_FOR_DELIVERY' ? 'bg-blue-100 text-blue-800' :
                  orderStatus === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                  orderStatus === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {orderStatus}
                </span>
              </div>
            </div>
          </div>
          
          {/* Price Breakdown */}
          <div className="border-b pb-4 mb-4">
            <h3 className="font-semibold mb-3">Price Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">₹{subTotal.toLocaleString('en-IN')}</span>
              </div>
              
              {discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span className="font-medium">−₹{discount.toLocaleString('en-IN')}</span>
                </div>
              )}
              
              <div className="flex justify-between">
                <span className="text-gray-600">Shipping Charges</span>
                <span className="font-medium">
                  {shippingCost > 0 ? `₹${shippingCost.toLocaleString('en-IN')}` : <span className="text-green-600">Free</span>}
                </span>
              </div>
              
              {gst > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">GST (18%)</span>
                  <span className="font-medium">₹{gst.toLocaleString('en-IN')}</span>
                </div>
              )}
              
              <div className="flex justify-between pt-3 border-t-2 font-bold text-lg">
                <span>Total Paid</span>
                <span className="text-green-600">₹{totalAmount.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </div>
          
          {/* Shipping Address */}
          {order?.shippingAddress && (
            <div>
              <h3 className="font-semibold mb-2">Shipping Address</h3>
              <div className="bg-gray-50 p-3 rounded text-sm">
                <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
                <p className="text-gray-700 mt-1">
                  {order.shippingAddress.addressLine1}
                  {order.shippingAddress.addressLine2 && `, ${order.shippingAddress.addressLine2}`}
                </p>
                {order.shippingAddress.landmark && (
                  <p className="text-gray-600 text-xs">Near: {order.shippingAddress.landmark}</p>
                )}
                <p className="text-gray-700">
                  {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
                </p>
                <p className="text-gray-700 mt-1">
                  <span className="font-medium">Phone:</span> {order.shippingAddress.phone}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={`/order/${orderId}`}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition text-center shadow-sm"
          >
            View Order Details
          </Link>
          
          <Link
            to="/orders"
            className="bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-300 transition text-center"
          >
            My Orders
          </Link>
          
          <Link
            to="/products"
            className="bg-white border border-gray-300 text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition text-center"
          >
            Continue Shopping
          </Link>
        </div>
        
        {/* Additional Info */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            What's Next?
          </h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              You'll receive an order confirmation email with payment receipt
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Track your order status anytime in the 'My Orders' section
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Your order will be prepared and dispatched soon
            </li>
            <li className="flex items-start">
              <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              We'll notify you when your order is out for delivery
            </li>
          </ul>
        </div>
        
        {/* Payment Security Note */}
        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 flex items-start gap-2">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-green-800">
            <span className="font-medium">Secure Payment Confirmed</span>
            <p className="mt-0.5">Your payment was processed securely through PhonePe. Transaction details have been sent to your email.</p>

          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;